/**
 * Chiamate alle API dei pagamenti (ADR 0027), servizio `billing` dietro il gateway.
 *
 * Nessun dato di carta passa da qui: per pagare la dashboard riceve l'indirizzo del
 * Checkout ospitato da Stripe e ci manda il browser; per gestire l'abbonamento riceve
 * l'indirizzo del Customer Portal. Al ritorno dal Checkout la pagina di esito chiama
 * `syncCheckout`, che rilegge la sessione da Stripe e applica piano o minuti: serve
 * anche quando il webhook non è ancora arrivato.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3), presidiati
 * da `billing.test.ts`.
 */

import type { ApiClient } from './client';

/** Piani acquistabili. */
export type PaidPlan = 'base' | 'premium';

/** Codice del 409 quando c'è già un abbonamento attivo: si cambia dal portale. */
export const SUBSCRIPTION_EXISTS = 'subscription_exists';

/** Codice del 409 quando c'è già la licenza singola di DevHub attiva (ADR 0065). */
export const DEVHUB_SUBSCRIPTION_EXISTS = 'devhub_subscription_exists';

/** Codice del 409 quando l'utente non ha mai pagato: il portale non serve. */
export const NO_CUSTOMER = 'no_customer';

/** Codice del 422 quando la richiesta di Checkout non dice il paese di fatturazione. */
export const COUNTRY_REQUIRED = 'country_required';

/** Codice del 422 per un privato di un paese in cui si vende solo ad aziende. */
export const CONSUMER_COUNTRY_NOT_SUPPORTED = 'consumer_country_not_supported';

/** Codice del 422 per una ricarica dei crediti fuori dai limiti (euro interi da 5 a 1.000). */
export const CREDITS_AMOUNT_INVALID = 'credits_amount_invalid';

/** Codice del 503 quando i pagamenti sono spenti. */
export const PAYMENTS_UNAVAILABLE = 'payments_unavailable';

/** Stati di un abbonamento che danno diritto al piano. */
export const ACTIVE_STATUSES = ['active', 'trialing', 'past_due'] as const;

/** Un piano in abbonamento del catalogo. */
export interface CatalogPlan {
  /** Piano. */
  plan: PaidPlan;
  /** Prezzo mensile in centesimi. */
  unit_amount: number;
  /** Valuta: `eur`, o `usd` fuori dall'area euro (ADR 0064). */
  currency: string;
  /** Ricorrenza, `month`. */
  interval: string;
  /** Set del piano (listino in vigore, ADR 0058). */
  max_sets?: number;
  /** Progetti per set. */
  max_projects_per_set?: number;
  /** Token conteggiati al mese. */
  monthly_token_limit?: number;
}

/** Un prodotto una tantum del catalogo. */
export interface CatalogProduct {
  /** Identificativo, es. `voice_minutes_60`. */
  product: string;
  /** Prezzo in centesimi. */
  unit_amount: number;
  /** Valuta. */
  currency: string;
  /** Credito accreditato, es. `voice_minutes`. */
  credit_kind: string;
  /** Quantità accreditata. */
  credit_amount: number;
}

/** Un taglio della ricarica dei crediti. */
export interface CreditDenomination {
  /** Importo in unità intere della valuta dell'offerta (il nome resta dall'euro). */
  amount_eur: number;
  /** Importo in centesimi. */
  unit_amount: number;
  /** Crediti accreditati, bonus compreso. */
  credits: number;
  /** Crediti in più rispetto alla tariffa base (0, 10, 20). */
  bonus_percent: number;
}

/** Una fascia di bonus della ricarica. */
export interface CreditBonusTier {
  /** Importo minimo della fascia, in euro. */
  min_amount_eur: number;
  /** Bonus della fascia. */
  bonus_percent: number;
}

/**
 * Ricarica dei crediti (ADR 0046). La tabella sta in `billing`: la pagina mostra i tagli
 * e, per l'importo libero, calcola l'anteprima con `creditsForAmount`. I crediti
 * accreditati li ricalcola comunque il servizio dall'importo pagato.
 */
export interface CreditsOffer {
  /** Valuta: `eur` o `usd`. */
  currency: string;
  /** Crediti per unità della valuta senza bonus (100 per euro, 93 per dollaro). */
  credits_per_euro: number;
  /** Fasce di bonus, dalla più bassa. */
  bonus_tiers: CreditBonusTier[];
  /** Tagli proposti, dal più piccolo. */
  denominations: CreditDenomination[];
  /** Importo libero minimo, in euro interi. */
  custom_min_eur: number;
  /** Importo libero massimo, in euro interi. */
  custom_max_eur: number;
}

/** Licenza singola di InstitorAI DevHub nel catalogo: abbonamento mensile solo per l'app. */
export interface CatalogDevhub {
  /** Prezzo mensile in centesimi. */
  unit_amount: number;
  /** Valuta. */
  currency: string;
  /** Ricorrenza: sempre `month`. */
  interval: string;
}

/** La licenza singola di DevHub dell'utente. */
export interface DevhubSubscription {
  /** Stato su Stripe. */
  status: string;
  /** True se dà diritto all'app. */
  active: boolean;
  /** Fine del periodo pagato, ISO 8601. */
  current_period_end: string | null;
  /** True se disdetta a fine periodo. */
  cancel_at_period_end: boolean;
}

/** Catalogo dei pagamenti. */
export interface BillingCatalog {
  /** False se i pagamenti sono spenti. */
  available: boolean;
  /** True se il Checkout usa Stripe come venditore (Managed Payments). */
  managed_payments: boolean;
  /** Piani, dal più piccolo. */
  plans: CatalogPlan[];
  /** Prodotti una tantum in vendita. */
  products: CatalogProduct[];
  /** Ricarica dei crediti. Assente nei servizi vecchi. */
  credits?: CreditsOffer;
  /** Paesi (ISO alpha-2) in cui si vende solo ad aziende. Assente nei servizi vecchi. */
  consumer_blocked_countries?: string[];
  /** Valuta di tutti gli importi (ADR 0064): `eur`, o `usd` fuori dall'area euro. */
  currency?: string;
  /** Valuta già fissata dall'abbonamento dell'utente: vale per ogni acquisto. */
  customer_currency?: string | null;
  /** Paesi (ISO alpha-2) con i prezzi in euro. */
  euro_countries?: string[];
  /** Licenza singola di DevHub (ADR 0065); assente da un servizio di prima. */
  devhub?: CatalogDevhub | null;
}

/** L'abbonamento dell'utente. */
export interface Subscription {
  /** Piano del prezzo, vuoto se fuori catalogo. */
  plan: string;
  /** Stato su Stripe: `active`, `past_due`, `canceled`… */
  status: string;
  /** Fine del periodo pagato, ISO 8601. */
  current_period_end: string | null;
  /** True se è disdetto a fine periodo. */
  cancel_at_period_end: boolean;
  /** `applied`, oppure `admin_override` se il piano resta quello di un amministratore. */
  plan_sync: string;
}

/** Un acquisto una tantum. */
export interface Purchase {
  /** Prodotto. */
  product: string;
  /** `pending`, `paid`, `failed`, `partially_refunded`, `refunded`, `rejected`. */
  status: string;
  /** Importo pagato in centesimi. */
  amount_total: number;
  /** Importo rimborsato in centesimi. */
  amount_refunded: number;
  /** Valuta. */
  currency: string;
  /** Istante dell'acquisto, ISO 8601. */
  created_at: string;
}

/** Situazione dei pagamenti dell'utente. */
export interface MyBilling {
  /** False se i pagamenti sono spenti. */
  available: boolean;
  /** True se l'utente ha già pagato almeno una volta: il portale ha senso. */
  has_customer: boolean;
  /** L'abbonamento che conta, o `null`. */
  subscription: Subscription | null;
  /** Ultimi acquisti. */
  purchases: Purchase[];
  /** Saldo per tipo di credito. */
  credits: Record<string, number>;
  /** Licenza singola di DevHub (attiva, o la più recente); `null` o assente se mai comprata. */
  devhub?: DevhubSubscription | null;
}

/** Una fattura di Stripe (ADR 0030 §7). */
export interface Invoice {
  /** Id su Stripe. */
  id: string;
  /** Numero; `null` finché Stripe non lo assegna. */
  number: string | null;
  /** Emissione, ISO 8601. */
  created_at: string;
  /** Totale in centesimi. */
  total: number;
  /** Pagato in centesimi. */
  amount_paid: number;
  /** Dovuto in centesimi. */
  amount_due: number;
  /** Valuta. */
  currency: string;
  /** `open`, `paid`, `uncollectible`, `void`. */
  status: string;
  /** Pagina della fattura ospitata da Stripe. */
  hosted_invoice_url: string | null;
  /** PDF ospitato da Stripe. */
  invoice_pdf: string | null;
}

/** Fatture dell'utente. */
export interface Invoices {
  /** False se i pagamenti sono spenti. */
  available: boolean;
  /** Dalla più recente. */
  items: Invoice[];
}

/** Dove riportare l'utente dopo Checkout e portale. */
export type ReturnTo = 'web' | 'app';

/** Che cosa si acquista: un abbonamento, un prodotto una tantum o una ricarica dei crediti. */
export type CheckoutTarget =
  | { kind: 'subscription'; plan: PaidPlan; return_to?: ReturnTo }
  | { kind: 'one_time'; product: string; return_to?: ReturnTo }
  | { kind: 'credits'; amount_eur: number; return_to?: ReturnTo }
  | { kind: 'devhub'; return_to?: ReturnTo };

/** Corpo di `POST /api/v1/billing/checkout`. */
export type CheckoutRequest = CheckoutTarget & {
  /**
   * Casella obbligatoria prima del pagamento: l'utente chiede l'inizio immediato e sa che,
   * se recede entro 14 giorni, paga la parte già usata (termini di servizio, §5). Senza,
   * il servizio risponde 422.
   */
  immediate_start_consent: boolean;
  /** Paese di fatturazione dichiarato (ISO alpha-2). Obbligatorio per il servizio. */
  country: string;
  /** True se compra come azienda: il Checkout chiede la partita IVA. */
  business: boolean;
  /** Lingua dell'utente, per la pagina di Stripe e le sue email. */
  locale?: 'it' | 'en' | 'de' | 'es' | 'ru' | 'ko' | 'th';
};

/** Sessione di Checkout creata. */
export interface CheckoutStart {
  /** Id della sessione, per la sincronizzazione al ritorno. */
  session_id: string;
  /** Pagina di pagamento di Stripe. */
  url: string;
}

/** Esito della sincronizzazione di una sessione. */
export interface CheckoutSync {
  /** Sessione. */
  session_id: string;
  /** `subscription` oppure `one_time`. */
  kind: string;
  /** `open`, `complete`, `expired`. */
  status: string;
  /** `paid`, `unpaid`, `no_payment_required`. */
  payment_status: string;
  /** Piano dell'abbonamento. */
  plan: string | null;
  /** Stato dell'abbonamento, se c'è. */
  subscription_status: string | null;
  /** `applied` o `admin_override`. */
  plan_sync: string | null;
  /** Prodotto una tantum. */
  product: string | null;
  /** Stato dell'acquisto. */
  purchase_status: string | null;
}

/**
 * True se l'abbonamento dà diritto al piano.
 *
 * @param subscription Abbonamento, o `null`.
 * @returns True per `active`, `trialing`, `past_due`.
 */
export function isActiveSubscription(subscription: Subscription | null): boolean {
  return (
    subscription !== null &&
    (ACTIVE_STATUSES as readonly string[]).includes(subscription.status)
  );
}

/**
 * Bonus di una ricarica, dalle fasce del catalogo.
 *
 * @param offer Offerta della ricarica.
 * @param amountEur Importo in euro.
 * @returns La percentuale di crediti in più.
 */
export function bonusPercent(offer: CreditsOffer, amountEur: number): number {
  let bonus = 0;
  for (const tier of offer.bonus_tiers) {
    if (amountEur >= tier.min_amount_eur) {
      bonus = tier.bonus_percent;
    }
  }
  return bonus;
}

/**
 * True se l'importo è una ricarica ammessa: euro interi fra minimo e massimo.
 *
 * @param offer Offerta della ricarica.
 * @param amountEur Importo, o `null`.
 * @returns L'esito.
 */
export function isValidTopup(offer: CreditsOffer, amountEur: number | null): amountEur is number {
  return (
    amountEur !== null &&
    Number.isInteger(amountEur) &&
    amountEur >= offer.custom_min_eur &&
    amountEur <= offer.custom_max_eur
  );
}

/**
 * Anteprima dei crediti di una ricarica, con la stessa regola di `billing`.
 *
 * @param offer Offerta della ricarica.
 * @param amountEur Importo in euro interi.
 * @returns I crediti, per difetto.
 */
export function creditsForAmount(offer: CreditsOffer, amountEur: number): number {
  return Math.floor((amountEur * offer.credits_per_euro * (100 + bonusPercent(offer, amountEur))) / 100);
}

/**
 * Importo leggibile.
 *
 * @param cents Importo in centesimi.
 * @param currency Valuta ISO, es. `usd`.
 * @param language Lingua dell'interfaccia.
 * @returns L'importo formattato, senza decimali se interi.
 */
export function formatAmount(cents: number, currency: string, language: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency.toUpperCase(),
    // «53 $» anche in italiano, non «53 USD» (ADR 0064).
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Operazioni sui pagamenti, esposte alla UI. */
/** Codice del 409: consumo a fine mese non ancora attivo sulla piattaforma. */
export const USAGE_BILLING_UNAVAILABLE = 'usage_billing_unavailable';

/** Codice del 409: il consumo a fine mese vuole un abbonamento attivo. */
export const USAGE_REQUIRES_SUBSCRIPTION = 'usage_requires_subscription';

/** Codice del 422: tetto fuori dai limiti. */
export const USAGE_CAP_INVALID = 'usage_cap_invalid';

/** Voci del consumo a fine mese. */
export type UsageKind = 'tokens' | 'voice_minutes' | 'storage_gib_days';

/** Una voce del periodo in corso. */
export interface UsageItem {
  /** Voce. */
  kind: UsageKind;
  /** Unità in fattura: blocchi da 100.000 token, minuti, GiB al giorno. */
  quantity: number;
  /** Importo in centesimi. */
  amount_cents: number;
}

/** Consumo a fine mese del chiamante (ADR 0058 §5). */
export interface UsageBilling {
  /** False se la piattaforma non lo ha ancora acceso. */
  available: boolean;
  /** True con un abbonamento Base o Premium attivo. */
  eligible: boolean;
  /** True se acceso. */
  enabled: boolean;
  /** Tetto del periodo, in centesimi. */
  cap_cents: number;
  /** Tetto minimo. */
  min_cap_cents: number;
  /** Tetto massimo. */
  max_cap_cents: number;
  /** Spesa del periodo. */
  spent_cents: number;
  /** Stima a fine periodo. */
  estimate_cents: number;
  /** Inizio del periodo. */
  period_start: string | null;
  /** Fine del periodo: arriva la fattura. */
  period_end: string | null;
  /** `payment_failed` se si è spento da solo. */
  disabled_reason: string;
  /** Prezzi, IVA esclusa. */
  prices: {
    token_block_cents: number;
    voice_minute_cents: number;
    storage_gib_month_cents: number;
  };
  /** Voci del periodo. */
  items: UsageItem[];
  /** Valuta di prezzi, tetto e spesa: quella del canone (ADR 0064). Assente: euro. */
  currency?: string;
}

export class BillingApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Catalogo dei piani e dei prodotti, con gli importi nella valuta chiesta (ADR 0064). Se
   * l'utente ha già una valuta fissata dal suo abbonamento, il servizio risponde in quella.
   *
   * @param currency Valuta voluta (`eur` o `usd`); senza, l'euro.
   * @returns Il catalogo.
   */
  catalog(currency?: string): Promise<BillingCatalog> {
    const query = currency === undefined ? '' : `?currency=${encodeURIComponent(currency)}`;
    return this.client.request<BillingCatalog>('GET', `/api/v1/billing/catalog${query}`);
  }

  /**
   * Situazione dei pagamenti del chiamante.
   *
   * @returns Abbonamento, acquisti e crediti.
   */
  me(): Promise<MyBilling> {
    return this.client.request<MyBilling>('GET', '/api/v1/billing/me');
  }

  /**
   * Ultime fatture del chiamante, con i link ospitati da Stripe.
   *
   * @returns `{available, items}`.
   */
  invoices(): Promise<Invoices> {
    return this.client.request<Invoices>('GET', '/api/v1/billing/invoices');
  }

  /**
   * Apre una sessione di Checkout.
   *
   * @param request Abbonamento, acquisto una tantum o ricarica dei crediti.
   * @returns Id e indirizzo della sessione.
   * @throws ApiError 409 `subscription_exists`, 422 `credits_amount_invalid`,
   *   503 `payments_unavailable`.
   */
  startCheckout(request: CheckoutRequest): Promise<CheckoutStart> {
    return this.client.request<CheckoutStart>('POST', '/api/v1/billing/checkout', {
      return_to: 'web',
      ...request,
    });
  }

  /**
   * Rilegge una sessione di Checkout e ne applica gli effetti.
   *
   * @param sessionId Id della sessione.
   * @returns Lo stato della sessione e degli effetti.
   * @throws ApiError 404 se la sessione non esiste o è di un altro utente.
   */
  syncCheckout(sessionId: string): Promise<CheckoutSync> {
    return this.client.request<CheckoutSync>(
      'POST',
      `/api/v1/billing/checkout/${encodeURIComponent(sessionId)}/sync`,
    );
  }

  /**
   * Consumo a fine mese del chiamante.
   *
   * @returns Stato, tetto, spesa e prezzi.
   */
  usage(): Promise<UsageBilling> {
    return this.client.request<UsageBilling>('GET', '/api/v1/billing/usage');
  }

  /**
   * Accende o spegne il consumo a fine mese, o cambia il tetto.
   *
   * @param enabled Acceso o spento.
   * @param capCents Tetto in centesimi, per accendere.
   * @returns Lo stato aggiornato.
   * @throws ApiError 409 `usage_requires_subscription`, 422 `usage_cap_invalid`.
   */
  setUsage(enabled: boolean, capCents?: number): Promise<UsageBilling> {
    return this.client.request<UsageBilling>('PUT', '/api/v1/billing/usage', {
      enabled,
      ...(capCents === undefined ? {} : { cap_cents: capCents }),
    });
  }

  /**
   * Apre il Customer Portal.
   *
   * @param returnTo Dove tornare.
   * @returns L'indirizzo del portale.
   * @throws ApiError 409 `no_customer` se l'utente non ha mai pagato.
   */
  portal(returnTo: ReturnTo = 'web'): Promise<{ url: string }> {
    return this.client.request<{ url: string }>('POST', '/api/v1/billing/portal', {
      return_to: returnTo,
    });
  }
}
